import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { UserInfo } from '@/types/api'
import {
  canAccessRoute as canAccessRouteForUser,
  getUserPermissions,
  getUserRoles,
  hasAnyRole as hasAnyUserRole,
  hasPermission as hasUserPermission,
  hasRole as hasUserRole,
  type RoleCode,
  type RouteAccessTarget,
} from '@/utils/permissions'

const TOKEN_STORAGE_KEY = 'token'
const USER_INFO_STORAGE_KEY = 'userInfo'

const getStorage = () => typeof window === 'undefined' ? null : window.localStorage

const readStoredUserInfo = (): UserInfo | null => {
  const raw = getStorage()?.getItem(USER_INFO_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as UserInfo
  } catch (error) {
    console.warn('parse stored userInfo failed', error)
    getStorage()?.removeItem(USER_INFO_STORAGE_KEY)
    return null
  }
}

export const useUserStore = defineStore('user', () => {
  // State
  const token = ref<string>(getStorage()?.getItem(TOKEN_STORAGE_KEY) || '')
  const userInfo = ref<UserInfo | null>(readStoredUserInfo())

  // Getters
  const isLoggedIn = computed(() => !!token.value)
  const roles = computed(() => getUserRoles(userInfo.value))
  const permissions = computed(() => getUserPermissions(userInfo.value))

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken
    getStorage()?.setItem(TOKEN_STORAGE_KEY, newToken)
  }

  const setUserInfo = (info: UserInfo) => {
    userInfo.value = info
    getStorage()?.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(info))
  }

  const clearUserInfo = () => {
    token.value = ''
    userInfo.value = null
    getStorage()?.removeItem(TOKEN_STORAGE_KEY)
    getStorage()?.removeItem(USER_INFO_STORAGE_KEY)
  }

  const hasPermission = (permission: string | readonly string[]): boolean =>
    hasUserPermission(userInfo.value, permission)

  const hasRole = (role: RoleCode): boolean => hasUserRole(userInfo.value, role)

  const hasAnyRole = (roleCodes: readonly RoleCode[]): boolean =>
    hasAnyUserRole(userInfo.value, roleCodes)

  const canAccessRoute = (target: RouteAccessTarget): boolean =>
    canAccessRouteForUser(target, userInfo.value, { fallbackAllow: isLoggedIn.value })

  return {
    token,
    userInfo,
    isLoggedIn,
    roles,
    permissions,
    setToken,
    setUserInfo,
    clearUserInfo,
    hasPermission,
    hasRole,
    hasAnyRole,
    canAccessRoute,
  }
})
